import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';
import { CookieOptions } from '../../shared/app.models';
import { CryptoUtil } from '../../shared/utilities/crypto.utils';

@Injectable({
  providedIn: 'root',
})
export class CustomStorageService {
  private readonly encryptKeys = environment.production;
  private readonly encryptValues = environment.production;

  async setLocal<T>(key: string, value: T): Promise<void> {
    const storageKey = await this.resolveKey(key);
    const storageValue = await this.serialize(value, storageKey);

    localStorage.setItem(storageKey, storageValue);
  }

  async getLocal<T>(key: string): Promise<T | null> {
    const storageKey = await this.resolveKey(key);
    const value = localStorage.getItem(storageKey);

    if (!value) {
      return null;
    }

    try {
      return await this.deserialize<T>(value, storageKey);
    } catch {
      return null;
    }
  }

  async removeLocal(key: string): Promise<void> {
    localStorage.removeItem(await this.resolveKey(key));
  }

  async hasLocal(key: string): Promise<boolean> {
    return localStorage.getItem(await this.resolveKey(key)) !== null;
  }

  clearLocal(): void {
    localStorage.clear();
  }

  async setSession<T>(key: string, value: T): Promise<void> {
    const storageKey = await this.resolveKey(key);
    const storageValue = await this.serialize(value, storageKey);

    sessionStorage.setItem(storageKey, storageValue);
  }

  async getSession<T>(key: string): Promise<T | null> {
    const storageKey = await this.resolveKey(key);
    const value = sessionStorage.getItem(storageKey);

    if (!value) {
      return null;
    }

    try {
      return await this.deserialize<T>(value, storageKey);
    } catch {
      return null;
    }
  }

  async removeSession(key: string): Promise<void> {
    sessionStorage.removeItem(await this.resolveKey(key));
  }

  async hasSession(key: string): Promise<boolean> {
    return sessionStorage.getItem(await this.resolveKey(key)) !== null;
  }

  clearSession(): void {
    sessionStorage.clear();
  }

  clearAll(): void {
    localStorage.clear();
    sessionStorage.clear();
  }

  async setCookie<T>(key: string, value: T, options: CookieOptions = {}): Promise<void> {
    const cookieKey = await this.resolveKey(key);
    const cookieValue = await this.serialize(value, cookieKey);

    let cookie = `${encodeURIComponent(cookieKey)}=${encodeURIComponent(cookieValue)}`;

    if (options.expires) {
      cookie += `; expires=${options.expires.toUTCString()}`;
    }

    if (options.maxAge !== undefined) {
      cookie += `; max-age=${options.maxAge}`;
    }

    cookie += `; path=${options.path ?? '/'}`;

    if (options.domain) {
      cookie += `; domain=${options.domain}`;
    }

    if (options.secure) {
      cookie += '; secure';
    }

    if (options.sameSite) {
      cookie += `; samesite=${options.sameSite}`;
    }

    document.cookie = cookie;
  }

  async getCookie<T>(key: string): Promise<T | null> {
    const cookieKey = await this.resolveKey(key);

    const cookies = document.cookie.split(';');

    const cookie = cookies.find((item) => {
      const [name] = item.trim().split('=');
      return decodeURIComponent(name) === cookieKey;
    });

    if (!cookie) {
      return null;
    }

    try {
      const [, ...valueParts] = cookie.split('=');
      const value = decodeURIComponent(valueParts.join('='));

      return await this.deserialize<T>(value, cookieKey);
    } catch {
      return null;
    }
  }

  async removeCookie(key: string, path = '/'): Promise<void> {
    const cookieKey = await this.resolveKey(key);

    document.cookie =
      `${encodeURIComponent(cookieKey)}=; ` +
      `expires=Thu, 01 Jan 1970 00:00:00 GMT; ` +
      `path=${path}`;
  }

  async hasCookie(key: string): Promise<boolean> {
    const cookieKey = await this.resolveKey(key);

    return document.cookie.split(';').some((item) => {
      const [name] = item.trim().split('=');
      return decodeURIComponent(name) === cookieKey;
    });
  }

  private async resolveKey(key: string): Promise<string> {
    if (!this.encryptKeys) {
      return key;
    }

    return CryptoUtil.hashWithKey(key, environment.secret);
  }

  private async serialize<T>(value: T, key: string): Promise<string> {
    const json = JSON.stringify(value);

    if (!this.encryptValues) {
      return json;
    }

    return CryptoUtil.encrypt(json, key);
  }

  private async deserialize<T>(value: string, key: string): Promise<T> {
    const json = this.encryptValues ? await CryptoUtil.decrypt(value, key) : value;

    return JSON.parse(json) as T;
  }
}
